/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CssDeclarationMapType, CssVariablesMapType } from '../interfaces/css-maps.interfaces';
import { CSS_VARS_HOST_TOKEN } from '../tokens/css-vars-host.token';
import { THEME_TOKEN } from '../tokens/theme.token';

@Injectable()
export class CssVariablesService {
  private readonly styleDeclaration$ = this.theme$.pipe(map(() => this.getComputedStyle()));

  constructor(
    @Inject(CSS_VARS_HOST_TOKEN)
    private readonly hostColorsElement: HTMLElement,
    @Inject(THEME_TOKEN)
    private readonly theme$: Observable<string>,
  ) {}

  public getPropertiesValue$<T>(varsMap: CssVariablesMapType<T>): Observable<CssDeclarationMapType<T>> {
    return this.styleDeclaration$.pipe(map((declaration) => this.getPropertiesMap<T>(varsMap, declaration)));
  }

  private getComputedStyle(): CSSStyleDeclaration {
    return getComputedStyle(this.hostColorsElement);
  }

  private getPropertiesMap<T>(
    varsMap: CssVariablesMapType,
    styleDeclaration: CSSStyleDeclaration,
  ): CssDeclarationMapType<T> {
    const declarationMap = {} as CssDeclarationMapType;

    const variables = Object.keys(varsMap) as (keyof CssVariablesMapType)[];

    variables.forEach((variable) => {
      const key = varsMap[variable];
      if (key === undefined) return;
      declarationMap[variable] = getPropertyValue(key, styleDeclaration);
    });

    return declarationMap;
  }
}

function getPropertyValue(property: string, styleDeclaration: CSSStyleDeclaration): string {
  return styleDeclaration.getPropertyValue(property).trim();
}
